import {
  HabitSchedule,
  PlannedOccurrence,
  ScheduleEntryState,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  endOfDay,
  format,
  isAfter,
  isBefore,
  startOfDay,
} from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { isBetween } from '../lib/date-fns';
import { getOccurrences } from './get-occurrences';
import { getScheduleEndDate } from './get-schedule-end-date';
import { getScheduleTimeframesByDateRange } from './get-schedule-timeframe-by-date';

const dateFormat = 'yyyy-MM-dd';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
  strictEndDate?: boolean;
};

export const getRrulePlanningBySchedule = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
    strictEndDate,
  }: Options,
) => {
  // figure out where to start from
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleEnd = getScheduleEndDate(schedule);

  const { skippedDates, completedDates, lastEntryDate } =
    schedule.entries.reduce<{
      skippedDates: Set<string>;
      completedDates: Set<string>;
      lastEntryDate?: Timestamp;
    }>(
      (acc, entry) => {
        // get the skipped dates to exclude them from the plannedOccurrences
        if (
          entry.state === ScheduleEntryState.Skip ||
          entry.state === ScheduleEntryState.Failure
        ) {
          acc.skippedDates.add(format(entry.date, dateFormat));
        }

        // get the completed dates to exclude them from the plannedOccurrences
        if (
          entry.state === ScheduleEntryState.Complete &&
          (entry.value ?? 0) >= schedule.completionTarget.count &&
          uncompletedOnly
        ) {
          acc.completedDates.add(format(entry.date, dateFormat));
        }

        if (
          sinceLastEntryOnly &&
          ((entry.state === ScheduleEntryState.Complete &&
            (entry.value ?? 0) >= schedule.completionTarget.count) ||
            entry.state !== ScheduleEntryState.Complete)
        ) {
          acc.lastEntryDate =
            !acc.lastEntryDate ||
            acc.lastEntryDate.getTime() < entry.date.getTime()
              ? entry.date
              : acc.lastEntryDate;
        }

        return acc;
      },
      {
        skippedDates: new Set(),
        completedDates: new Set(),
        lastEntryDate: undefined,
      },
    );

  const _start = new Date(
    Math.max(
      // get the later date of the two; given startDate or schedule start date
      startDate.getTime(),
      scheduleStartDate.getTime(),
      lastEntryDate?.getTime() ?? startDate.getTime(),
    ),
  );
  // figure out where to end
  const _end = new Date(
    Math.min(endDate.getTime(), scheduleEnd?.getTime() ?? endDate.getTime()),
  );

  // if there are timeframes from before the startDate (entries entered before the start of the schedule) we also take those
  const preTimeframes =
    _start.getTime() < scheduleStartDate.getTime()
      ? getScheduleTimeframesByDateRange(schedule, {
          startDate: _start,
          endDate: endOfDay(addDays(scheduleStartDate, -1)),
          weekStartsOn,
        })
      : [];

  const duringTimeframes = getScheduleTimeframesByDateRange(schedule, {
    startDate: scheduleStartDate,
    endDate: _end,
    weekStartsOn,
  });

  // get a list of all unique timeframes
  const timeframes = [
    ...new Map(
      [...preTimeframes, ...duringTimeframes].map((t) => [
        `${t.startDate.getTime()}:${t.endDate.getTime()}`,
        t,
      ]),
    ).values(),
  ];

  // get rrule occurrences
  const rruleOccurrences = getOccurrences([schedule], {
    startDate: _start,
    endDate: _end,
  });

  // loop over timeframes
  return timeframes.reduce<PlannedOccurrence[]>((acc, timeframe) => {
    const plannedOccurrences =
      schedule.plannedOccurrences
        ?.filter(({ date }) =>
          isBetween(
            date,
            timeframe.startDate,
            strictEndDate ? endDate : timeframe.endDate,
          ),
        )
        .sort(
          (plannedA, plannedB) =>
            plannedA.date.getTime() - plannedB.date.getTime(),
        ) ?? [];

    if (plannedOccurrences.length) {
      acc.push(
        ...plannedOccurrences.filter(
          ({ date }) =>
            !skippedDates.has(format(date, dateFormat)) &&
            !completedDates.has(format(date, dateFormat)) &&
            (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
            // occurrences from before the start date are not wanted
            !isBefore(date, startDate) &&
            // occurrences after the end date are not wanted
            !isAfter(date, _end),
        ),
      );

      return acc;
    }

    // get the occurrences between start- and end-date and map those to plannedOccurrences
    for (const date of rruleOccurrences) {
      if (
        !skippedDates.has(format(date, dateFormat)) &&
        !completedDates.has(format(date, dateFormat)) &&
        (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
        isBetween(
          date,
          timeframe.startDate,
          strictEndDate && isAfter(timeframe.endDate, endDate)
            ? endDate
            : timeframe.endDate,
        ) &&
        // occurrences from before the start date are not wanted
        !isBefore(date, startDate) &&
        // occurrences after the end date are not wanted
        !isAfter(date, _end)
      ) {
        acc.push({ id: uuidv4(), date, timeslot: schedule.timeslot ?? null });
      }
    }

    return acc;
  }, []);
};
